import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { formatTournamentCenterStageStartDateByLeagueConfig } from '@/components/tournamentCenter/tournamentCenterHelper';
import { useInterval } from '@/composable/useInterval';
import { useState } from '@/composable/useState';
import { Api } from '@/core/lib/api';
import { getOddsApiUrl, getOddsApiWsUrl } from '@/core/lib/url';
import {
    expiredDateToEnable,
    toArray,
    toEnable,
    toEnumArray,
    toFloat,
    toInteger,
    toNumberArray,
    toObject,
    toPromotionPeriodOrNull,
    toTrimmedArray,
} from '@/core/oddsApi/helpers/toggleHelper';
import type { IPromotionPeriod } from '@/core/oddsApi/helpers/toggleHelper';
import type { IBehaviorMinutes } from '@/interface/IBehaviorMinutes';
import type { ILiveScoreProviderConfig } from '@/interface/ILiveScoreProviderConfig';
import type { IBetBuilderCombinationSelectionDisplayRule, IBetBuilderLegSetting } from '@/interface/betBuilder';
import type { BehaviorType, PlayerType, TournamentStage } from '@/interface/enum';
import { SiteStyle, SportType } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';

export { toObject } from '@/core/oddsApi/helpers/toggleHelper';

/**
 * Variation of useState, with a setter that protects against undefined values and parses input string using the provided parser function.
 * @param initialState
 * @param parser function to parse string into the generic type T
 */
function useFeatureToggleState<T>(initialState: T, parser = (stringValue: string) => stringValue as T): [Ref<T>, (stringValue?: string) => void] {
    const [state, _setState] = useState(initialState);

    const setState = (stringValue?: string) => {
        if (stringValue === undefined) return;

        try {
            const parsed = parser(stringValue);
            _setState(parsed);
        } catch (e) {
            console.error('Failed to parse feature toggle state', stringValue, e);
        }
    };

    return [state, setState];
}

const _toggles = () => {
    /**
     * @internal This is only used to check if the toggle store is initialized with the correct siteStyle.
     *
     * You should not use this value for any other purpose.
     */
    const [_siteStyle, setSiteType] = useState(SiteStyle.Unknown);

    const [isInLiveStreamWhiteList, setIsInLiveStreamWhiteList] = useFeatureToggleState(false, toEnable);
    const [esportsNotAllowCurrencies, setEsportsNotAllowCurrencies] = useFeatureToggleState<string[]>([], toArray);
    const [isESportsImInSportEnabled, setIsESportsImInSportEnabled] = useFeatureToggleState(false, toEnable);
    const [isMuayThaiOneBxInSportEnabled, setIsMuayThaiOneBxInSportEnabled] = useFeatureToggleState(false, toEnable);
    const [muayThaiOneBxAllowedPlayerTypes, setMuayThaiOneBxAllowedPlayerTypes] = useFeatureToggleState<PlayerType[]>([], toEnumArray);
    const [isGLiveStreamEnabled, setIsGLiveStreamEnabled] = useFeatureToggleState(false, toEnable);
    const [isAvApiLiveStreamEnabled, setIsAvApiLiveStreamEnabled] = useFeatureToggleState(false, toEnable);
    const [liveStreamRefreshSeconds, setLiveStreamRefreshSeconds] = useFeatureToggleState(30, toInteger);
    const [gLiveStreamRestrictions, setGLiveStreamRestrictions] = useFeatureToggleState<string[]>([], s => toArray(s, '|'));
    const [excludeSportList, setExcludeSportList] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [defaultOpenMoreMarketInLiveLeagueIds, setDefaultOpenMoreMarketInLiveLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [defaultOpenMoreMarketInLiveBySportsIds, setDefaultOpenMoreMarketInLiveBySportsIds] = useFeatureToggleState([], toEnumArray);
    const [supportFailedBetReasonSports, setSupportFailedBetReasonSports] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [liveScoreRefreshSeconds, setLiveScoreRefreshSeconds] = useFeatureToggleState(30, toInteger);
    const [miniMyBetRefreshSeconds, setMiniMyBetRefreshSeconds] = useFeatureToggleState(120, toInteger);
    const [miniMyBetWaitingRefreshSeconds, setMiniMyBetWaitingRefreshSeconds] = useFeatureToggleState(5, toInteger);
    const [myBetRefreshSeconds, setMyBetRefreshSeconds] = useFeatureToggleState(120, toInteger);
    const [myBetWaitingRefreshSeconds, setMyBetWaitingRefreshSeconds] = useFeatureToggleState(5, toInteger);
    const [multipleSportsLeagueNames, setMultipleSportsLeagueNames] = useFeatureToggleState<string[]>([], toArray);
    const [eFootballZoneLeagueIds, setEFootballZoneLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [simulationSportsZoneLeagueIds, setSimulationSportsZoneLeagueIds] = useFeatureToggleState<Partial<Record<SportType, number[]>>>({}, value => toObject<Partial<Record<SportType, number[]>>>(value, {}));
    const [simulationSportsZoneSupportedSports, setSimulationSportsZoneSupportedSports] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [isCasinoWidgetEnabled, setIsCasinoWidgetEnabled] = useFeatureToggleState(false, toEnable);
    const [isMiniGameWidgetEnabled, setIsMiniGameWidgetEnabled] = useFeatureToggleState(false, toEnable);
    const [isVirtualFootballEnabled, setIsVirtualFootballEnabled] = useFeatureToggleState(false, toEnable);
    const [isVirtualBasketballEnabled, setIsVirtualBasketballEnabled] = useFeatureToggleState(false, toEnable);
    const [isVoucherEnabled, setIsVoucherEnabled] = useFeatureToggleState(false, toEnable);
    const [cashOutValueRefreshSeconds, setCashOutValueRefreshSeconds] = useFeatureToggleState(5, toInteger);
    const [isCashOutEnabled, setCashOutEnabled] = useFeatureToggleState(false, toEnable);
    const [liveTicketAutoRefreshSeconds, setLiveTicketAutoRefreshSeconds] = useFeatureToggleState(3, toInteger);
    const [nonLiveTicketAutoRefreshSeconds, setNonLiveTicketAutoRefreshSeconds] = useFeatureToggleState(5, toInteger);
    const [isSendBehaviorEnabled, setIsSendBehaviorEnabled] = useFeatureToggleState(false, toEnable);
    const [behaviorMinutes, setBehaviorMinutes] = useFeatureToggleState<IBehaviorMinutes>({}, s => toObject(s, {}));
    const [miniMyBetMaxDisplayCount, setMiniMyBetMaxDisplayCount] = useFeatureToggleState(15, toInteger);
    const [isShowTvChannelsTooltip, setIsShowTvChannelsTooltip] = useFeatureToggleState(false, toEnable);
    const [liveRefreshDuration, setLiveRefreshDuration] = useFeatureToggleState(20, parseInt);
    const [nonLiveRefreshDuration, setNonLiveRefreshDuration] = useFeatureToggleState(60, parseInt);
    const [oddsApiUrl, setOddsApiUrl] = useFeatureToggleState('');
    const [oddsApiWsUrl, setOddsApiWsUrl] = useFeatureToggleState('');
    const [recommendationApiUrl, setRecommendationApiUrl] = useFeatureToggleState('');
    const [recommendationWsUrl, setRecommendationWsUrl] = useFeatureToggleState('');
    const [isBetBuilderEnabled, setIsBetBuilderEnabled] = useFeatureToggleState(false, toEnable);
    const [isJacktimeBetBuilderEnabled, setIsJacktimeBetBuilderEnabled] = useFeatureToggleState(false, toEnable);
    const [_jacktimeBetBuilderSupportedLeagueIds, setJacktimeBetBuilderSupportedLeagueIds] = useFeatureToggleState('');
    const [betBuilderMinLegCount, setBetBuilderMinLegCount] = useFeatureToggleState(0, parseInt);
    const [betBuilderMinLegSettings, setBetBuilderMinLegSettings] = useFeatureToggleState<IBetBuilderLegSetting[]>([], setting => toObject(setting, []));
    const [placeBetDelayTime, setPlaceBetDelayTime] = useFeatureToggleState(0, parseInt);
    const [liveScoreProviderConfig, setLiveScoreProviderConfig] = useFeatureToggleState<ILiveScoreProviderConfig>({}, state => toObject(state, {}));
    const [setGameScoreSportList, setSetGameScoreSportList] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [quarterSportList, setQuarterSportList] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [periodSportList, setPeriodSportList] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [inningSportList, setInningSportList] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [scoringTypeASportType, setScoringTypeASportType] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [isShowMultipleSportsHotLink, setIsShowMultipleSportsHotLink] = useFeatureToggleState(false, expiredDateToEnable);
    const [isUseNewPlaceBetApi, setIsUseNewPlaceBetApi] = useFeatureToggleState(false, toEnable);
    const [mpTwoSubBetLeagueIds, setMpTwoSubBetLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [isMpTwoSubBetEnabled, setIsMpTwoSubBetEnabled] = useFeatureToggleState(false, toEnable);
    const [isJackpotEnabled, setIsJackpotEnabled] = useFeatureToggleState(false, toEnable);
    const [multipleSportsZoneSupportSports, setMultipleSportsZoneSupportSports] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [toggleAutoRefreshSeconds, setToggleAutoRefreshSeconds] = useFeatureToggleState(60, toInteger);
    const [isLiveStreamBanner1Enabled, setIsLiveStreamBanner1Enabled] = useFeatureToggleState(false, toEnable);
    const [isLiveStreamBanner2Enabled, setIsLiveStreamBanner2Enabled] = useFeatureToggleState(false, toEnable);
    const [liveStreamBannerEnableMatchIds, setLiveStreamBannerEnableMatchIds] = useFeatureToggleState([], toNumberArray);
    const [isBetBuilderTutorialEnabled, setIsBetBuilderTutorialEnabled] = useFeatureToggleState(false, toEnable);
    const [isBetBuilderResultEnabled, setIsBetBuilderResultEnabled] = useFeatureToggleState(false, toEnable);
    const [isBetBuilderDisplayPriorityEnabled, setIsBetBuilderDisplayPriorityEnabled] = useFeatureToggleState(false, toEnable);
    const [defaultSportType, setDefaultSportType] = useFeatureToggleState(SportType.Soccer, toInteger);
    const [isFairExchangeEnabled, setIsFairExchangeEnabled] = useFeatureToggleState(false, toEnable);
    const [isIdleMonitorEnabled, setIsIdleMonitorEnabled] = useFeatureToggleState(false, toEnable);
    const [isCricketEnabled, setIsCricketEnabled] = useFeatureToggleState(false, toEnable);
    const [isInEFootballLeagueBlackList, setIsInEFootballLeagueBlackList] = useFeatureToggleState(false, toEnable);
    const [filteredEFootballLeagueIds, setFilteredEFootballLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [isFantasySelectionEnabled, setIsFantasySelectionEnabled] = useFeatureToggleState(false, toEnable);
    const [isMpCombinationEnabled, setIsMpCombinationEnabled] = useFeatureToggleState(false, toEnable);
    const [betGeniusCustomerId, setBetGeniusCustomerId] = useFeatureToggleState('');
    const [ePenaltyLeagueIds, setEPenaltyLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [tournamentCenterLeagueNames, setTournamentCenterLeagueNames] = useFeatureToggleState<string[]>([], toArray);
    const [tournamentCenterMainLeagueIds, setTournamentCenterMainLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [isTournamentCenterEnabled, setIsTournamentCenterEnabled] = useFeatureToggleState(false, expiredDateToEnable);
    const [_isPromotionEnabled, setIsPromotionEnabled] = useFeatureToggleState(false, toEnable);
    const [isPromotionNotExpired, setIsPromotionNotExpired] = useFeatureToggleState(false, expiredDateToEnable);
    const [tournamentCenterGroupWinnerLeagueName, setTournamentCenterGroupWinnerLeagueName] = useFeatureToggleState('');
    const [tournamentCenterStandingLeagueIds, setTournamentCenterStandingLeagueIds] = useFeatureToggleState([], toNumberArray);
    const [tournamentCenterStageStartDateByLeagueConfig, setTournamentCenterStageStartDateByLeagueConfig] = useFeatureToggleState<Map<number, Map<TournamentStage, Date>>>(new Map(), formatTournamentCenterStageStartDateByLeagueConfig);
    const [jerseyDisplayMatchExclusionRule, setJerseyDisplayMatchExclusionRule] = useFeatureToggleState('');
    const [apmServerUrl, setApmServerUrl] = useFeatureToggleState('');
    const [isApmEnabled, setIsApmEnabled] = useFeatureToggleState(false, toEnable);
    const [asiaApmSampleRate, setAsiaApmSampleRate] = useFeatureToggleState(0, toFloat);
    const [euroApmSampleRate, setEuroApmSampleRate] = useFeatureToggleState(0, toFloat);
    const [isJackpotEFootballInBigEventTheme, setIsJackpotEFootballInBigEventTheme] = useFeatureToggleState(false, toEnable);
    const [isEFootballInTournamentCenterEnabled, setIsEFootballInTournamentCenterEnabled] = useFeatureToggleState(false, toEnable);
    const [dynamicMarketsSportTypes, setDynamicMarketsSportTypes] = useFeatureToggleState<SportType[]>([], toEnumArray);
    const [isDynamicMarketEnabled, setIsDynamicMarketEnabled] = useFeatureToggleState(false, toEnable);
    const [isDynamicMarketMTokenEnabled, setIsDynamicMarketMTokenEnabled] = useFeatureToggleState(false, toEnable);
    const [isUseSoccerTodayExpand, setIsUseSoccerTodayExpand] = useFeatureToggleState(false, toEnable);
    const [isStatisticsEnabled, setIsStatisticsEnabled] = useFeatureToggleState(false, toEnable);
    const [isPrebuiltInMoreMarketEnabled, setIsPrebuiltInMoreMarketEnabled] = useFeatureToggleState(false, toEnable);
    const [isSwitchEFootballToSimulationSportsZoneEnabled, setisSwitchEFootballToSimulationSportsZoneEnabled] = useFeatureToggleState(false, toEnable);
    // Score Prediction promotion (TCSP-8390 / TCSP-8392), global settings shared with Castor (Jason, 2026-10-06):
    // Member_ScorePredictionLeagueNames (comma separated) and
    // Member_ScorePredictionPromotionPeriod = { "start": ISO-8601 with offset, "end": ISO-8601 with offset }.
    // Pollux forwards Member_* settings to every project with the prefix stripped and the key camel-cased.
    const [scorePredictionLeagueNames, setScorePredictionLeagueNames] = useFeatureToggleState<string[]>([], toTrimmedArray);
    const [scorePredictionPromotionPeriod, setScorePredictionPromotionPeriod] = useFeatureToggleState<IPromotionPeriod | null>(null, toPromotionPeriodOrNull);

    const defaultMoreMarketPrebuiltDisplayRule: IBetBuilderCombinationSelectionDisplayRule = {
        selectionCount: 2,
        combinationCount: 2,
        oddsRange: [
            { min: 3, max: 5, limit: 2 },
            { min: 0, max: 3 },
            { min: 3, max: null },
        ],
    };

    const [moreMarketPrebuiltDisplayRule, setMoreMarketPrebuiltDisplayRule] = useFeatureToggleState<IBetBuilderCombinationSelectionDisplayRule>(
        defaultMoreMarketPrebuiltDisplayRule,
        value => toObject(value, defaultMoreMarketPrebuiltDisplayRule),
    );

    const isPromotionEnabled = computed(() => _isPromotionEnabled.value && isPromotionNotExpired.value);
    async function initToggle(siteStyle: SiteStyle) {
        try {
            setSiteType(siteStyle);

            const { data: toggleResponse } = await Api.getToggle(siteStyle);
            setEsportsNotAllowCurrencies(toggleResponse.esportsNotAllowCurrencies);
            setIsInLiveStreamWhiteList(toggleResponse.isInAvApiWhiteList);
            setIsESportsImInSportEnabled(toggleResponse.isESportsImInSportEnabled);
            setIsMuayThaiOneBxInSportEnabled(toggleResponse.isMuayThaiOneBxInSportEnabled);
            setMuayThaiOneBxAllowedPlayerTypes(toggleResponse.muayThaiOneBxAllowedPlayerTypes);
            setIsGLiveStreamEnabled(toggleResponse.isGLiveStreamEnabled);
            setIsAvApiLiveStreamEnabled(toggleResponse.isAvApiLiveStreamEnabled);
            setLiveStreamRefreshSeconds(toggleResponse.liveStreamRefreshSeconds);
            setGLiveStreamRestrictions(toggleResponse.gLiveStreamRestrictions);
            setExcludeSportList(toggleResponse.excludeSportList);
            setDefaultOpenMoreMarketInLiveLeagueIds(toggleResponse.defaultOpenMoreMarketInLiveLeagueIds);
            setDefaultOpenMoreMarketInLiveBySportsIds(toggleResponse.defaultOpenMoreMarketInLiveBySportsIds);
            setSupportFailedBetReasonSports(toggleResponse.supportFailedBetReasonSports);
            setLiveScoreRefreshSeconds(toggleResponse.liveScoreRefreshSeconds);
            setMiniMyBetRefreshSeconds(toggleResponse.miniMyBetRefreshSeconds);
            setMiniMyBetWaitingRefreshSeconds(toggleResponse.miniMyBetWaitingRefreshSeconds);
            setMyBetRefreshSeconds(toggleResponse.myBetRefreshSeconds);
            setMyBetWaitingRefreshSeconds(toggleResponse.myBetWaitingRefreshSeconds);
            setMultipleSportsLeagueNames(toggleResponse.multipleSportsTournamentNames);
            setEFootballZoneLeagueIds(toggleResponse.eFootballZoneLeagueIds);
            setSimulationSportsZoneLeagueIds(toggleResponse.simulationSportsZoneLeagueIds);
            setSimulationSportsZoneSupportedSports(toggleResponse.simulationSportsZoneSupportedSports);
            setIsCasinoWidgetEnabled(toggleResponse.isCasinoWidgetEnabled);
            setIsMiniGameWidgetEnabled(toggleResponse.isMiniGameWidgetEnabled);
            setIsVirtualFootballEnabled(toggleResponse.isVirtualFootballEnabled);
            setIsVirtualBasketballEnabled(toggleResponse.isVirtualBasketballEnabled);
            setIsVoucherEnabled(toggleResponse.isVoucherEnabled);
            setCashOutValueRefreshSeconds(toggleResponse.cashOutValueRefreshSeconds);
            setCashOutEnabled(toggleResponse.isCashOutEnabled);
            setLiveTicketAutoRefreshSeconds(toggleResponse.liveTicketAutoRefreshSeconds);
            setNonLiveTicketAutoRefreshSeconds(toggleResponse.nonLiveTicketAutoRefreshSeconds);
            setIsSendBehaviorEnabled(toggleResponse.isSendBehaviorEnabled);
            setBehaviorMinutes(toggleResponse.behaviorMinutes);
            setMiniMyBetMaxDisplayCount(toggleResponse.miniMyBetMaxDisplayCount);
            setIsShowTvChannelsTooltip(toggleResponse.isShowTvChannelsTooltip);
            setLiveRefreshDuration(toggleResponse.liveRefreshDuration);
            setNonLiveRefreshDuration(toggleResponse.nonLiveRefreshDuration);
            setOddsApiUrl(toggleResponse.oddsApiUrl);
            setOddsApiWsUrl(toggleResponse.oddsApiWsUrl);
            setRecommendationApiUrl(getOddsApiUrl(toggleResponse.recommendationApiUrl));
            setRecommendationWsUrl(getOddsApiWsUrl(toggleResponse.recommendationWsUrl));
            setIsBetBuilderEnabled(toggleResponse.isBetBuilderEnabled);
            setIsJacktimeBetBuilderEnabled(toggleResponse.isJtBetBuilderEnabled);
            setJacktimeBetBuilderSupportedLeagueIds(toggleResponse.jtBetBuilderSupportedLeagueIds);
            setBetBuilderMinLegCount(toggleResponse.betBuilderMinLegCount);
            setBetBuilderMinLegSettings(toggleResponse.betBuilderMinLegSettings);
            setPlaceBetDelayTime(toggleResponse.placeBetDelayTime);
            setLiveScoreProviderConfig(toggleResponse.liveScoreProviderConfig);
            setSetGameScoreSportList(toggleResponse.setGameScoreSportList);
            setQuarterSportList(toggleResponse.quarterSportList);
            setPeriodSportList(toggleResponse.periodSportList);
            setInningSportList(toggleResponse.inningSportList);
            setScoringTypeASportType(toggleResponse.scoringTypeASportType);
            setIsShowMultipleSportsHotLink(toggleResponse.multipleSportsHotLinkExpiredDate);
            setIsUseNewPlaceBetApi(toggleResponse.isUseNewPlaceBetApi);
            setMpTwoSubBetLeagueIds(toggleResponse.mpTwoSubBetLeagueIds);
            setIsMpTwoSubBetEnabled(toggleResponse.isTwoMpSubBetEnabled);
            setIsJackpotEnabled(toggleResponse.isJackpotEnabled);
            setMultipleSportsZoneSupportSports(toggleResponse.multipleSportsZoneSupportSports);
            setToggleAutoRefreshSeconds(toggleResponse.toggleAutoRefreshSeconds);
            setIsLiveStreamBanner1Enabled(toggleResponse.isLiveStreamBanner1Enabled);
            setIsLiveStreamBanner2Enabled(toggleResponse.isLiveStreamBanner2Enabled);
            setLiveStreamBannerEnableMatchIds(toggleResponse.liveStreamBannerEnableMatchIds);
            setIsBetBuilderTutorialEnabled(toggleResponse.isBetBuilderTutorialEnabled);
            setIsBetBuilderResultEnabled(toggleResponse.isBetBuilderResultEnabled);
            setIsBetBuilderDisplayPriorityEnabled(toggleResponse.isBetBuilderDisplayPriorityEnabled);
            setDefaultSportType(toggleResponse.defaultSportType);
            setIsFairExchangeEnabled(toggleResponse.isFairExchangeEnabled);
            setIsIdleMonitorEnabled(toggleResponse.isIdleMonitorEnabled);
            setIsCricketEnabled(toggleResponse.isCricketEnabled);
            setIsInEFootballLeagueBlackList(toggleResponse.isInEFootballLeagueBlackList);
            setFilteredEFootballLeagueIds(toggleResponse.filteredEFootballLeagueIds);
            setIsFantasySelectionEnabled(toggleResponse.isFantasySelectionEnabled);
            setIsMpCombinationEnabled(toggleResponse.isMpCombinationEnabled);
            setBetGeniusCustomerId(toggleResponse.betGeniusCustomerId);
            setEPenaltyLeagueIds(toggleResponse.epenaltyLeagueIds);
            setTournamentCenterLeagueNames(toggleResponse.tournamentCenterLeagueNames);
            setIsTournamentCenterEnabled(toggleResponse.tournamentCenterExpiredDate);
            setIsPromotionEnabled(toggleResponse.isPromotionEnabled);
            setIsPromotionNotExpired(toggleResponse.promotionExpiredDate);
            setTournamentCenterMainLeagueIds(toggleResponse.tournamentCenterMainLeagueIds);
            setTournamentCenterGroupWinnerLeagueName(toggleResponse.tournamentCenterGroupWinnerLeagueName);
            setTournamentCenterStandingLeagueIds(toggleResponse.tournamentCenterStandingLeagueIds);
            setTournamentCenterStageStartDateByLeagueConfig(toggleResponse.tournamentCenterStageStartDateByLeagueConfig);
            setJerseyDisplayMatchExclusionRule(toggleResponse.jerseyDisplayMatchExclusionRule);
            setApmServerUrl(toggleResponse.apmServerUrl);
            setIsApmEnabled(toggleResponse.isApmEnabled);
            setAsiaApmSampleRate(toggleResponse.asiaApmSampleRate);
            setEuroApmSampleRate(toggleResponse.euroApmSampleRate);
            setIsJackpotEFootballInBigEventTheme(toggleResponse.isJackpotEFootballInBigEventTheme);
            setIsEFootballInTournamentCenterEnabled(toggleResponse.isEFootballInTournamentCenterEnabled);
            setDynamicMarketsSportTypes(toggleResponse.showDynamicMarketsSportTypes);
            setIsDynamicMarketEnabled(toggleResponse.isDynamicMarketEnabled);
            setIsDynamicMarketMTokenEnabled(toggleResponse.isDynamicMarketMTokenEnabled);
            setIsUseSoccerTodayExpand(toggleResponse.isUseSoccerTodayExpand);
            setIsPrebuiltInMoreMarketEnabled(toggleResponse.isPrebuiltInMoreMarketEnabled);
            setMoreMarketPrebuiltDisplayRule(toggleResponse.moreMarketPrebuiltDisplayRule);
            setIsStatisticsEnabled(toggleResponse.isStatisticsEnabled);
            setisSwitchEFootballToSimulationSportsZoneEnabled(toggleResponse.isSwitchEFootballToSimulationSportsZoneEnabled);
            setScorePredictionLeagueNames(toggleResponse.scorePredictionLeagueNames);
            setScorePredictionPromotionPeriod(toggleResponse.scorePredictionPromotionPeriod);
        } catch (error) {
            console.error('initToggle: ', error);
        }
    }

    const { siteStyle } = storeToRefs(useCustomerStore());
    function registerAutoRefresh() {
        const isEnabledAutoRefresh = computed(() => toggleAutoRefreshSeconds.value > 0);

        useInterval(
            () => {
                initToggle(siteStyle.value);
            },
            toggleAutoRefreshSeconds,
            {
                isEnabled: isEnabledAutoRefresh,
            },
        );
    }

    function getBehaviorMinutes(type: BehaviorType): number {
        const config = behaviorMinutes.value;
        if (type in config) {
            return config[type] ?? 0;
        }
        return 0;
    }

    function getBetBuilderMinSelectionCount(leagueId: number) {
        return betBuilderMinLegSettings.value.find(setting => setting.leagueId === leagueId)?.minBetBuilderLegs ?? betBuilderMinLegCount.value;
    }

    function isJacktimeBetBuilderSupportedLeague(leagueId: number) {
        if (_jacktimeBetBuilderSupportedLeagueIds.value === '*') return true;
        return toNumberArray(_jacktimeBetBuilderSupportedLeagueIds.value).includes(leagueId);
    }

    return {
        initToggle,
        registerAutoRefresh,
        _siteStyle,

        esportsNotAllowCurrencies,
        isESportsImInSportEnabled,
        isMuayThaiOneBxInSportEnabled,
        muayThaiOneBxAllowedPlayerTypes,
        isGLiveStreamEnabled,
        isAvApiLiveStreamEnabled,
        gLiveStreamRestrictions,
        liveStreamRefreshSeconds,
        excludeSportList,
        defaultOpenMoreMarketInLiveLeagueIds,
        defaultOpenMoreMarketInLiveBySportsIds,
        isInLiveStreamWhiteList,
        supportFailedBetReasonSports,
        liveScoreRefreshSeconds,
        miniMyBetRefreshSeconds,
        miniMyBetWaitingRefreshSeconds,
        myBetRefreshSeconds,
        myBetWaitingRefreshSeconds,
        eFootballZoneLeagueIds,
        simulationSportsZoneLeagueIds,
        simulationSportsZoneSupportedSports,
        isCasinoWidgetEnabled,
        isMiniGameWidgetEnabled,
        isVirtualFootballEnabled,
        isVirtualBasketballEnabled,
        isVoucherEnabled,
        isCashOutEnabled,
        cashOutValueRefreshSeconds,
        liveTicketAutoRefreshSeconds,
        nonLiveTicketAutoRefreshSeconds,
        isSendBehaviorEnabled,
        miniMyBetMaxDisplayCount,
        isShowTvChannelsTooltip,
        liveRefreshDuration,
        nonLiveRefreshDuration,
        oddsApiUrl,
        oddsApiWsUrl,
        recommendationApiUrl,
        recommendationWsUrl,
        isBetBuilderEnabled,
        isJacktimeBetBuilderEnabled,
        isJacktimeBetBuilderSupportedLeague,
        getBetBuilderMinSelectionCount,
        placeBetDelayTime,
        liveScoreProviderConfig,
        getBehaviorMinutes,
        setGameScoreSportList,
        quarterSportList,
        periodSportList,
        inningSportList,
        scoringTypeASportType,
        multipleSportsLeagueNames,
        isShowMultipleSportsHotLink,
        isUseNewPlaceBetApi,
        mpTwoSubBetLeagueIds,
        isMpTwoSubBetEnabled,
        isJackpotEnabled,
        multipleSportsZoneSupportSports,
        isLiveStreamBanner1Enabled,
        isLiveStreamBanner2Enabled,
        liveStreamBannerEnableMatchIds,
        toggleAutoRefreshSeconds,
        isBetBuilderTutorialEnabled,
        betBuilderMinLegCount,
        isBetBuilderResultEnabled,
        isBetBuilderDisplayPriorityEnabled,
        defaultSportType,
        isFairExchangeEnabled,
        isIdleMonitorEnabled,
        isCricketEnabled,
        isInEFootballLeagueBlackList,
        filteredEFootballLeagueIds,
        isFantasySelectionEnabled,
        isMpCombinationEnabled,
        isPromotionEnabled,
        betGeniusCustomerId,
        ePenaltyLeagueIds,
        tournamentCenterLeagueNames,
        tournamentCenterMainLeagueIds,
        isTournamentCenterEnabled,
        tournamentCenterGroupWinnerLeagueName,
        tournamentCenterStandingLeagueIds,
        tournamentCenterStageStartDateByLeagueConfig,
        jerseyDisplayMatchExclusionRule,
        apmServerUrl,
        isApmEnabled,
        asiaApmSampleRate,
        euroApmSampleRate,
        isJackpotEFootballInBigEventTheme,
        isEFootballInTournamentCenterEnabled,
        dynamicMarketsSportTypes,
        isDynamicMarketEnabled,
        isDynamicMarketMTokenEnabled,
        isUseSoccerTodayExpand,
        isPrebuiltInMoreMarketEnabled,
        moreMarketPrebuiltDisplayRule,
        isStatisticsEnabled,
        isSwitchEFootballToSimulationSportsZoneEnabled,
        scorePredictionLeagueNames,
        scorePredictionPromotionPeriod,
    };
};

type AllToggles = ReturnType<typeof _toggles>;

type AsiaTogglesWhitelist = keyof Pick<
AllToggles,
| 'initToggle'
| 'registerAutoRefresh'
| 'esportsNotAllowCurrencies'
| 'isESportsImInSportEnabled'
| 'isMuayThaiOneBxInSportEnabled'
| 'muayThaiOneBxAllowedPlayerTypes'
| 'isGLiveStreamEnabled'
| 'isAvApiLiveStreamEnabled'
| 'gLiveStreamRestrictions'
| 'liveStreamRefreshSeconds'
| 'excludeSportList'
| 'defaultOpenMoreMarketInLiveLeagueIds'
| 'defaultOpenMoreMarketInLiveBySportsIds'
| 'isInLiveStreamWhiteList'
| 'supportFailedBetReasonSports'
| 'liveScoreRefreshSeconds'
| 'miniMyBetRefreshSeconds'
| 'miniMyBetWaitingRefreshSeconds'
| 'myBetRefreshSeconds'
| 'myBetWaitingRefreshSeconds'
| 'eFootballZoneLeagueIds'
| 'simulationSportsZoneLeagueIds'
| 'simulationSportsZoneSupportedSports'
| 'isCasinoWidgetEnabled'
| 'isMiniGameWidgetEnabled'
| 'isVirtualFootballEnabled'
| 'isVirtualBasketballEnabled'
| 'isVoucherEnabled'
| 'isCashOutEnabled'
| 'cashOutValueRefreshSeconds'
| 'liveTicketAutoRefreshSeconds'
| 'nonLiveTicketAutoRefreshSeconds'
| 'isSendBehaviorEnabled'
| 'miniMyBetMaxDisplayCount'
| 'getBehaviorMinutes'
| 'liveRefreshDuration'
| 'nonLiveRefreshDuration'
| 'oddsApiUrl'
| 'oddsApiWsUrl'
| 'recommendationApiUrl'
| 'recommendationWsUrl'
| 'isBetBuilderEnabled'
| 'isJacktimeBetBuilderEnabled'
| 'isJacktimeBetBuilderSupportedLeague'
| 'getBetBuilderMinSelectionCount'
| 'placeBetDelayTime'
| 'liveScoreProviderConfig'
| 'setGameScoreSportList'
| 'quarterSportList'
| 'periodSportList'
| 'inningSportList'
| 'scoringTypeASportType'
| 'multipleSportsLeagueNames'
| 'isShowMultipleSportsHotLink'
| 'mpTwoSubBetLeagueIds'
| 'isMpTwoSubBetEnabled'
| 'isJackpotEnabled'
| 'multipleSportsZoneSupportSports'
| 'toggleAutoRefreshSeconds'
| 'isLiveStreamBanner1Enabled'
| 'isLiveStreamBanner2Enabled'
| 'liveStreamBannerEnableMatchIds'
| 'isBetBuilderTutorialEnabled'
| 'betBuilderMinLegCount'
| 'isBetBuilderResultEnabled'
| 'isBetBuilderDisplayPriorityEnabled'
| 'defaultSportType'
| 'isFairExchangeEnabled'
| 'isIdleMonitorEnabled'
| 'isCricketEnabled'
| 'isInEFootballLeagueBlackList'
| 'filteredEFootballLeagueIds'
| 'isFantasySelectionEnabled'
| 'isMpCombinationEnabled'
| 'betGeniusCustomerId'
| 'ePenaltyLeagueIds'
| 'tournamentCenterLeagueNames'
| 'tournamentCenterMainLeagueIds'
| 'isTournamentCenterEnabled'
| 'isPromotionEnabled'
| 'tournamentCenterGroupWinnerLeagueName'
| 'tournamentCenterStandingLeagueIds'
| 'tournamentCenterStageStartDateByLeagueConfig'
| 'jerseyDisplayMatchExclusionRule'
| 'isApmEnabled'
| 'apmServerUrl'
| 'asiaApmSampleRate'
| 'euroApmSampleRate'
| 'isJackpotEFootballInBigEventTheme'
| 'isEFootballInTournamentCenterEnabled'
| 'dynamicMarketsSportTypes'
| 'isDynamicMarketEnabled'
| 'isDynamicMarketMTokenEnabled'
| 'isUseSoccerTodayExpand'
| 'isPrebuiltInMoreMarketEnabled'
| 'moreMarketPrebuiltDisplayRule'
| 'isStatisticsEnabled'
| 'isSwitchEFootballToSimulationSportsZoneEnabled'
| 'scorePredictionLeagueNames'
| 'scorePredictionPromotionPeriod'
>;

type EuroTogglesWhitelist = keyof Pick<
AllToggles,
| 'initToggle'
| 'registerAutoRefresh'
| 'esportsNotAllowCurrencies'
| 'isESportsImInSportEnabled'
| 'isMuayThaiOneBxInSportEnabled'
| 'muayThaiOneBxAllowedPlayerTypes'
| 'isGLiveStreamEnabled'
| 'isAvApiLiveStreamEnabled'
| 'gLiveStreamRestrictions'
| 'liveStreamRefreshSeconds'
| 'excludeSportList'
| 'isInLiveStreamWhiteList'
| 'supportFailedBetReasonSports'
| 'myBetRefreshSeconds'
| 'myBetWaitingRefreshSeconds'
// | 'eFootballZoneLeagueIds'
// | 'isCasinoWidgetEnabled'
| 'isMiniGameWidgetEnabled'
| 'isVirtualFootballEnabled'
| 'isVirtualBasketballEnabled'
| 'isVoucherEnabled'
| 'isCashOutEnabled'
| 'cashOutValueRefreshSeconds'
| 'liveTicketAutoRefreshSeconds'
| 'nonLiveTicketAutoRefreshSeconds'
| 'isSendBehaviorEnabled'
| 'getBehaviorMinutes'
| 'liveRefreshDuration'
| 'nonLiveRefreshDuration'
| 'oddsApiUrl'
| 'oddsApiWsUrl'
| 'recommendationApiUrl'
| 'recommendationWsUrl'
| 'getBetBuilderMinSelectionCount'
| 'placeBetDelayTime'
| 'liveScoreProviderConfig'
| 'setGameScoreSportList'
| 'quarterSportList'
| 'periodSportList'
| 'inningSportList'
| 'scoringTypeASportType'
| 'isUseNewPlaceBetApi'
| 'mpTwoSubBetLeagueIds'
| 'isMpTwoSubBetEnabled'
| 'isJackpotEnabled'
| 'toggleAutoRefreshSeconds'
| 'isLiveStreamBanner1Enabled'
| 'isLiveStreamBanner2Enabled'
| 'liveStreamBannerEnableMatchIds'
| 'isBetBuilderResultEnabled'
| 'isBetBuilderDisplayPriorityEnabled'
| 'defaultSportType'
| 'isFairExchangeEnabled'
| 'isIdleMonitorEnabled'
| 'isCricketEnabled'
| 'isInEFootballLeagueBlackList'
| 'filteredEFootballLeagueIds'
| 'isFantasySelectionEnabled'
| 'betGeniusCustomerId'
| 'ePenaltyLeagueIds'
| 'tournamentCenterMainLeagueIds'
| 'tournamentCenterStandingLeagueIds'
| 'tournamentCenterStageStartDateByLeagueConfig'
| 'jerseyDisplayMatchExclusionRule'
| 'isPromotionEnabled'
| 'isApmEnabled'
| 'apmServerUrl'
| 'asiaApmSampleRate'
| 'euroApmSampleRate'
| 'isJackpotEFootballInBigEventTheme'
| 'isStatisticsEnabled'
| 'dynamicMarketsSportTypes'
| 'isPrebuiltInMoreMarketEnabled'
| 'moreMarketPrebuiltDisplayRule'
>;

type AsiaTogglesBlacklist = keyof Omit<AllToggles, AsiaTogglesWhitelist>;
type EuroTogglesBlacklist = keyof Omit<AllToggles, EuroTogglesWhitelist>;

const _useToggleStore = defineStore('Toggle', _toggles);

/** Store with all toggles. Should only use in places such as startup, shared components, or tests where choosing either {@link AsiaToggleStore} or {@link EuroToggleStore} is irrelevant. */
type ToggleStore = ReturnType<typeof _useToggleStore>;

/** Store with only whitelisted Asia site toggles. @see {@link AsiaTogglesWhitelist} */
type AsiaToggleStore = Omit<ToggleStore, AsiaTogglesBlacklist>; // use Omit blacklist instead of Pick whitelist to prevent breaking pinia store type

/** Store with only whitelisted Euro site toggles. @see {@link EuroTogglesWhitelist} */
type EuroToggleStore = Omit<ToggleStore, EuroTogglesBlacklist>; // use Omit blacklist instead of Pick whitelist to prevent breaking pinia store type

type IntersectToggleStore = Omit<ToggleStore, AsiaTogglesBlacklist | EuroTogglesBlacklist>;

/* eslint-disable padding-line-between-statements */
export function useToggleStore(siteStyle: SiteStyle.Asia): AsiaToggleStore
export function useToggleStore(siteStyle: SiteStyle.Euro): EuroToggleStore
export function useToggleStore(): IntersectToggleStore
export function useToggleStore(siteStyle?: SiteStyle) {
    const toggleStore = _useToggleStore();
    if (__ENV_DEV__) {
        if (siteStyle !== undefined && toggleStore._siteStyle !== SiteStyle.Unknown && siteStyle !== toggleStore._siteStyle) {
            console.warn(`useToggleStore: siteStyle mismatch. Expected ${toggleStore._siteStyle}, got ${siteStyle}`);
        }
    }

    return toggleStore;
}
/* eslint-enable padding-line-between-statements */
